import React, { useCallback, useEffect, useId, useRef } from 'react';
import { NavLink, useRouteMatch, useLocation } from 'react-router-dom';

import { snakeCase } from 'lodash';
import { DefaultTooltip, MenuItem, MenuSubitem, MenuSubitems, useSidebarContext } from 'twyne-new.ui';

import { useAppLayoutContext } from './context';

function isPathActive(pathname, path) {
  return pathname === path || pathname.startsWith(`${path}/`)
}

function LinkWrapper({ to, children }) {
  return to ? <NavLink to={to}>
    {children}
  </NavLink> : children
}

export default function AppSidebarSubitems({ icon, alias, children, to }) {
  const id = useId()
  const { expanded, setExpanded, collapsed } = useSidebarContext()
  const { pathname } = useLocation()
  const match = useRouteMatch(to)
  const isMatched = to ? !!match : false
  const matchedOnceRef = useRef(false)

  const isChildRouteActive = useCallback(() => React.Children.toArray(children).some(child => {
    if (child?.props?.to) {
      return isPathActive(pathname, child.props.to)
    }
    return false
  }), [children, pathname])

  const hasActiveChildRoute = isChildRouteActive()
  const active = expanded === id || isMatched || hasActiveChildRoute

  const { setMenuCollapsed } = useAppLayoutContext()
  useEffect(() => {
    if (isMatched || hasActiveChildRoute) {
      setMenuCollapsed(false)
    }
  }, [isMatched, hasActiveChildRoute, setMenuCollapsed])

  useEffect(() => {
    if (isMatched && to && !collapsed && !matchedOnceRef.current) {
      setExpanded(id)
    }
    matchedOnceRef.current = isMatched
  }, [isMatched, id, setExpanded, to, collapsed])
  return <>
    <DefaultTooltip content={!expanded && alias} wrapperProps={{ asChild: true }} side='right'>
      <LinkWrapper to={to}>
        <MenuItem icon={icon} active={active} onClick={() => setExpanded(prevId => prevId === id ? '' : id)}>
          {alias}
        </MenuItem>
      </LinkWrapper>
    </DefaultTooltip>
    <MenuSubitems collapsed={!active}>
      {children}
    </MenuSubitems>
  </>
}

export function AppSidebarSubitem({ to, alias, hasPermission, require }) {
  const { pathname } = useLocation()
  const active = isPathActive(pathname, to)

  if (hasPermission && require && !hasPermission(require)) {
    return null
  }

  const id = `tooltip_${snakeCase(alias)}`

  return <NavLink to={to} id={id}>
    <MenuSubitem active={active}>
      {alias}
    </MenuSubitem>
  </NavLink>
}
